import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';

import { tokens } from '../../../design';

/**
 * Shared layout primitives for the order detail tabs (AUT-123), mirroring
 * the users `ProfileTab` info-grid / stat-box shapes from the mockup.
 * Extracted so each tab file stays well under the component line cap.
 */

export const SectionTitle = ({ children }: { children: ReactNode }): ReactElement => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      color: tokens.text.tertiary,
      mb: 1.25,
    }}
  >
    {children}
  </Typography>
);

export const InfoItem = ({
  label,
  children,
  span,
}: {
  label: string;
  children: ReactNode;
  /** When true, the item spans the full grid width (e.g. the address). */
  span?: boolean;
}): ReactElement => (
  <Box sx={span ? { gridColumn: '1 / -1' } : undefined}>
    <Typography sx={{ fontSize: 11, color: tokens.text.tertiary, mb: 0.4 }}>{label}</Typography>
    <Box sx={{ fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {children}
    </Box>
  </Box>
);

export const InfoGrid = ({ children }: { children: ReactNode }): ReactElement => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px' }}>{children}</Box>
);

export const StatBox = ({ value, label }: { value: ReactNode; label: string }): ReactElement => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 1.75,
      bgcolor: 'background.paper',
    }}
  >
    <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px' }}>{value}</Typography>
    <Typography sx={{ fontSize: 11, color: tokens.text.tertiary, mt: 0.25 }}>{label}</Typography>
  </Box>
);
